/**
 * Parse a raw text string into a structured tree of paragraphs / sentences / words.
 * Each node gets a stable id so selection state can reference it.
 *
 * Word ids are formatted as `w-{paraIdx}-{sentIdx}-{wordIdx}` for easy lookup.
 */

export type Word = {
  id: string;
  text: string; // the word itself, no leading/trailing punctuation
  paragraphId: string;
  sentenceId: string;
  paraIdx: number;
  sentIdx: number;
  wordIdx: number;
  // Char positions inside the sentence string — used by the renderer to
  // reconstruct the exact trailing punctuation/whitespace between words.
  start: number;
  end: number;
};

export type Sentence = {
  id: string;
  text: string; // trimmed sentence text
  paragraphId: string;
  paraIdx: number;
  sentIdx: number;
  wordIds: string[];
};

export type Paragraph = {
  id: string;
  text: string;
  paraIdx: number;
  sentenceIds: string[];
  wordIds: string[];
};

export type ParsedText = {
  paragraphs: Paragraph[];
  sentences: Sentence[];
  words: Word[];
  totalWords: number;
  totalSentences: number;
  totalParagraphs: number;
};

// Split text into paragraphs by one or more blank lines.
function splitParagraphs(text: string): string[] {
  // Normalize CRLF -> LF, then split on 2+ newlines
  const normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  return normalized
    .split(/\n\s*\n+/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
}

// Split a paragraph into sentences. Keep it lightweight (no full NLP).
// We treat ., !, ? followed by whitespace/end as terminators.
function splitSentences(paragraph: string): string[] {
  // First, collapse single newlines inside a paragraph into spaces.
  const collapsed = paragraph.replace(/\n/g, " ").replace(/\s+/g, " ").trim();
  if (!collapsed) return [];

  const sentences: string[] = [];
  let current = "";
  for (let i = 0; i < collapsed.length; i++) {
    const ch = collapsed[i];
    current += ch;
    if (ch === "." || ch === "!" || ch === "?") {
      // peek next non-space char
      let j = i + 1;
      while (j < collapsed.length && /\s/.test(collapsed[j])) j++;
      // If next char is uppercase or end-of-string, treat as sentence boundary.
      // Also handle common abbreviations (Mr., Mrs., Dr., etc.) by skipping.
      const next = collapsed[j];
      const isAbbrev = /\b(Mr|Mrs|Ms|Dr|Prof|Sr|Jr|St|vs|etc|e\.g|i\.e|U\.S|U\.K)\.$/i.test(
        current.trim()
      );
      if (!isAbbrev && (next === undefined || /[A-Z"'\(]/.test(next))) {
        sentences.push(current.trim());
        current = "";
        i = j - 1;
      }
    }
  }
  if (current.trim().length > 0) sentences.push(current.trim());
  return sentences;
}

// Split a sentence into words. A word is a maximal run of word characters
// (letters, digits, apostrophes inside the word, hyphens inside the word).
// Returns each word with its char-range [start, end) inside the sentence.
function splitWords(sentence: string): { text: string; start: number; end: number }[] {
  const out: { text: string; start: number; end: number }[] = [];
  const regex = /([A-Za-z0-9](?:[A-Za-z0-9'\-]*[A-Za-z0-9])?)/g;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(sentence)) !== null) {
    const word = m[1];
    const start = m.index;
    out.push({ text: word, start, end: start + word.length });
  }
  return out;
}

export function parseText(raw: string): ParsedText {
  const paragraphs: Paragraph[] = [];
  const sentences: Sentence[] = [];
  const words: Word[] = [];

  const paraStrings = splitParagraphs(raw);

  paraStrings.forEach((paraText, paraIdx) => {
    const paragraphId = `p-${paraIdx}`;
    const sentenceIds: string[] = [];
    const wordIds: string[] = [];

    const sentenceStrings = splitSentences(paraText);

    sentenceStrings.forEach((sentText, sentIdx) => {
      const sentenceId = `s-${paraIdx}-${sentIdx}`;
      const sentWordIds: string[] = [];

      const wordParts = splitWords(sentText);
      wordParts.forEach((wp, wordIdx) => {
        const wordId = `w-${paraIdx}-${sentIdx}-${wordIdx}`;
        const word: Word = {
          id: wordId,
          text: wp.text,
          paragraphId,
          sentenceId,
          paraIdx,
          sentIdx,
          wordIdx,
          start: wp.start,
          end: wp.end,
        };
        words.push(word);
        sentWordIds.push(wordId);
        wordIds.push(wordId);
      });

      sentences.push({
        id: sentenceId,
        text: sentText,
        paragraphId,
        paraIdx,
        sentIdx,
        wordIds: sentWordIds,
      });
      sentenceIds.push(sentenceId);
    });

    paragraphs.push({
      id: paragraphId,
      text: paraText.replace(/\n/g, " ").replace(/\s+/g, " ").trim(),
      paraIdx,
      sentenceIds,
      wordIds,
    });
  });

  return {
    paragraphs,
    sentences,
    words,
    totalWords: words.length,
    totalSentences: sentences.length,
    totalParagraphs: paragraphs.length,
  };
}

// Helpers to navigate the parsed structure by id
export function findWord(parsed: ParsedText, id: string): Word | undefined {
  return parsed.words.find((w) => w.id === id);
}
export function findSentence(parsed: ParsedText, id: string): Sentence | undefined {
  return parsed.sentences.find((s) => s.id === id);
}
export function findParagraph(parsed: ParsedText, id: string): Paragraph | undefined {
  return parsed.paragraphs.find((p) => p.id === id);
}

// Given a word id, find the next/prev word in document order.
export function nextWordId(parsed: ParsedText, currentId: string): string | null {
  const idx = parsed.words.findIndex((w) => w.id === currentId);
  if (idx < 0 || idx + 1 >= parsed.words.length) return null;
  return parsed.words[idx + 1].id;
}
export function prevWordId(parsed: ParsedText, currentId: string): string | null {
  const idx = parsed.words.findIndex((w) => w.id === currentId);
  if (idx <= 0) return null;
  return parsed.words[idx - 1].id;
}
export function nextSentenceId(parsed: ParsedText, currentId: string): string | null {
  const idx = parsed.sentences.findIndex((s) => s.id === currentId);
  if (idx < 0 || idx + 1 >= parsed.sentences.length) return null;
  return parsed.sentences[idx + 1].id;
}
export function prevSentenceId(parsed: ParsedText, currentId: string): string | null {
  const idx = parsed.sentences.findIndex((s) => s.id === currentId);
  if (idx <= 0) return null;
  return parsed.sentences[idx - 1].id;
}
export function nextParagraphId(parsed: ParsedText, currentId: string): string | null {
  const idx = parsed.paragraphs.findIndex((p) => p.id === currentId);
  if (idx < 0 || idx + 1 >= parsed.paragraphs.length) return null;
  return parsed.paragraphs[idx + 1].id;
}
export function prevParagraphId(parsed: ParsedText, currentId: string): string | null {
  const idx = parsed.paragraphs.findIndex((p) => p.id === currentId);
  if (idx <= 0) return null;
  return parsed.paragraphs[idx - 1].id;
}
